import { z } from "zod";

import { quickRepliesPublicSchema } from "./quick-replies-public-config";

/** One greeting line (legacy + dashboard may store strings or objects). */
export const autoLaunchGreetMessageSchema = z.union([
  z.string().min(1).max(5000),
  z.object({
    id: z.string().optional(),
    text: z.string().min(1).max(5000),
    delay_ms: z.number().finite().nonnegative().optional(),
  }),
]);

export type AutoLaunchGreetMessageInput = z.infer<typeof autoLaunchGreetMessageSchema>;

export function normalizeGreetMessage(
  raw: AutoLaunchGreetMessageInput,
): { id?: string; text: string; delay_ms?: number } {
  if (typeof raw === "string") {
    return { text: raw.trim() };
  }
  return {
    id: raw.id,
    text: raw.text.trim(),
    delay_ms: raw.delay_ms,
  };
}

/** Trim → drop empties → dedupe (case-insensitive) for targeting arrays. */
function stringArraySchema(): z.ZodType<readonly string[]> {
  return z
    .array(z.string())
    .default([])
    .transform((arr) => {
      const seen = new Set<string>();
      const out: string[] = [];
      for (const raw of arr) {
        const v = String(raw ?? '').trim();
        if (!v) continue;
        const k = v.toLowerCase();
        if (seen.has(k)) continue;
        seen.add(k);
        out.push(v);
      }
      return out as readonly string[];
    });
}

const isoTimestampSchema = z
  .string()
  .nullable()
  .optional()
  .transform((v) => {
    if (!v) return null;
    const t = Date.parse(v);
    return Number.isFinite(t) ? v : null;
  });

export const autoLaunchPublicConfigSchema = z.object({
  config_id: z.string().uuid().nullable(),
  enabled: z.boolean(),
  greet_messages: z.array(autoLaunchGreetMessageSchema).default([]),
  frequency: z.string().min(1).default("once_per_session"),
  cooldown_sec: z.number().finite().nonnegative().nullable().optional(),
  max_impressions_per_session: z.number().finite().int().nonnegative().default(1),
  launch_delay_sec: z.number().finite().nonnegative().default(0),
  /** Legacy alias used by some clients; mirrors `launch_delay_sec`. */
  delay_sec: z.number().finite().nonnegative().optional(),
  message_order: z.string().min(1).default("sequential"),
  /** Path prefix targeting (e.g. ['/pricing']). `[]` means match all. */
  path_include: stringArraySchema(),
  path_exclude: stringArraySchema(),
  /** Subset of {'desktop','tablet','mobile'}. `[]` means match all. */
  device_include: stringArraySchema(),
  /** BCP-47 prefixes (e.g. ['en']). `[]` means match all. */
  locale_include: stringArraySchema(),
  /** ISO-8601 schedule window. Either may be null. */
  start_at: isoTimestampSchema,
  end_at: isoTimestampSchema,
  /**
   * Operator opt-in (#155 Item 4): when true, the widget suppresses
   * auto-launch for visitors whose `localStorage` carries a
   * "visitor engaged" hint set on the first user message of any
   * prior session on this site/tenant. Default false — greet fires
   * for everyone. Migrated TSAI tenants get this set to true by
   * the cutover script to preserve their pre-cutover UX.
   */
  suppress_for_returning_visitors: z.boolean().default(false),
  quick_replies: quickRepliesPublicSchema.optional(),
});

export type AutoLaunchPublicConfig = z.infer<typeof autoLaunchPublicConfigSchema>;

export const AUTO_LAUNCH_DISABLED: AutoLaunchPublicConfig = {
  config_id: null,
  enabled: false,
  greet_messages: [],
  frequency: "once_per_session",
  cooldown_sec: null,
  max_impressions_per_session: 1,
  launch_delay_sec: 0,
  delay_sec: 0,
  message_order: "sequential",
  path_include: [],
  path_exclude: [],
  device_include: [],
  locale_include: [],
  start_at: null,
  end_at: null,
  suppress_for_returning_visitors: false,
};

export function parseAutoLaunchPublicConfig(raw: unknown): AutoLaunchPublicConfig {
  const parsed = autoLaunchPublicConfigSchema.safeParse(raw);
  if (!parsed.success) {
    return { ...AUTO_LAUNCH_DISABLED };
  }
  const v = parsed.data;
  // Legacy alias back-fill: callers may send `delay_sec` (TSAI) or
  // `launch_delay_sec` (S2RP). Inspect the raw input so we can distinguish
  // "not provided" from "explicitly 0" — the schema default obscures this.
  const rawObj = (raw ?? {}) as Record<string, unknown>;
  const rawHasNew = typeof rawObj.launch_delay_sec === 'number';
  const rawHasLegacy = typeof rawObj.delay_sec === 'number';
  const launch_delay_sec = rawHasNew
    ? (v.launch_delay_sec ?? 0)
    : rawHasLegacy
      ? (v.delay_sec ?? 0)
      : 0;
  return {
    ...v,
    launch_delay_sec,
    delay_sec: v.delay_sec ?? launch_delay_sec,
  };
}
